import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { createRubric } from '@/api/rubrics/api';
import RubricForm from './components/RubricForm';
import type { RubricRequest } from '@/api/rubrics/types';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, AlertCircle, Loader2, Plus } from 'lucide-react';
import { itemVariants } from '@/components/animations';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { SiteHeader } from '@/components/site-header';
import { useAuth } from '@/context/AuthContext';
import { UnauthorizedAccess } from '@/components/auth/UnauthorizedAccess';

const RubricCreate = () => {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { error: showError, success } = useEnhancedToast();
    const { user } = useAuth();
    
    const canCreateRubric = user?.permissions?.rubrics === 'EDITOR';
    
    // Show unauthorized access if user doesn't have editor permission
    if (user && !canCreateRubric) {
        return <UnauthorizedAccess />;
    }

    const handleSubmit = async (data: RubricRequest) => {
        setIsLoading(true);
        try {
            const response = await createRubric(data);
            if (response.data) {
                success({
                    title: 'Rubric Created',
                    description: 'The rubric has been successfully created.',
                    icon: CheckCircle2
                });
                navigate('/dashboard/rubrics');
            } else if (response.error) {
                showError({
                    title: 'Creation Failed',
                    description: response.error,
                    icon: AlertCircle
                });
            }
        } catch {
            showError({
                title: 'Creation Failed',
                description: 'Could not create rubric',
                icon: AlertCircle
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <SiteHeader 
                title="Create New Rubric"
                subtitle="Configure your rubric settings and evaluation criteria."
                backButton={
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-1)}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                }
            >
                <Button 
                    type="submit"
                    form="rubric-form"
                    disabled={isLoading}
                    size="sm"
                    className="gap-2"
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Creating...
                        </>
                    ) : (
                        <>
                            <Plus className="w-4 h-4" />
                            Create Rubric
                        </>
                    )}
                </Button>
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-6 md:px-8 md:py-8"
                >
                    <RubricForm 
                        onSubmit={handleSubmit} 
                        formId="rubric-form"
                    />
                </motion.div>
            </div>
        </>
    );
};

export default RubricCreate; 